import { useState, useEffect } from 'react'
import { supabase, storage, db } from '../../lib/supabase'

export default function Website() {
  const [loading, setLoading] = useState(true)
  const [selectedPage, setSelectedPage] = useState('home')
  const [config, setConfig] = useState(null)
  const [saving, setSaving] = useState(false)
  const [heroImageFile, setHeroImageFile] = useState(null)
  const [heroImagePreview, setHeroImagePreview] = useState('')
  
  // Ubícanos states
  const [sliderImages, setSliderImages] = useState([])
  const [cards, setCards] = useState([])
  const [showSlideModal, setShowSlideModal] = useState(false)
  const [showCardModal, setShowCardModal] = useState(false)
  const [editingSlide, setEditingSlide] = useState(null)
  const [editingCard, setEditingCard] = useState(null)

  // Home cards states
  const [homeCards, setHomeCards] = useState([])
  const [showHomeCardModal, setShowHomeCardModal] = useState(false)
  const [editingHomeCard, setEditingHomeCard] = useState(null)

  // Home block states
  const [homeBlock, setHomeBlock] = useState(null)
  const [showHomeBlockModal, setShowHomeBlockModal] = useState(false)

  // SEO states
  const [seoPages, setSeoPages] = useState([])
  const [selectedSeoPage, setSelectedSeoPage] = useState('home')
  const [seoConfig, setSeoConfig] = useState(null)
  const [savingSeo, setSavingSeo] = useState(false)

  // Brand filters states (Home only)
  const [availableCategories, setAvailableCategories] = useState([])
  const [selectedFilters, setSelectedFilters] = useState([])

  const pages = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'tiendas', label: 'Tiendas', icon: '🏪' },
    { id: 'eventos', label: 'Eventos', icon: '🎉' },
    { id: 'descuentos', label: 'Descuentos', icon: '🏷️' },
    { id: 'ubicanos', label: 'Ubícanos', icon: '📍' },
    { id: 'seo', label: 'SEO', icon: '🔍' }
  ]

  useEffect(() => {
    if (selectedPage === 'ubicanos') {
      loadUbicanosData()
    } else if (selectedPage === 'seo') {
      loadSeoPages()
    } else {
      loadConfig()
      if (selectedPage === 'home') {
        loadHomeCards()
        loadHomeBlock()
        loadBrandFilters()
      }
    }
  }, [selectedPage])

  const loadConfig = async () => {
    if (selectedPage === 'ubicanos' || selectedPage === 'seo') return
    
    try {
      setLoading(true)
      const { data, error } = await db.getConfiguracionPortada(selectedPage)
      
      if (error) throw error
      
      if (data) {
        setConfig(data)
        setHeroImagePreview(data.hero_imagen_url || '')
        // Load selected filters if they exist
        if (data.brand_filters && Array.isArray(data.brand_filters)) {
          setSelectedFilters(data.brand_filters)
        } else {
          setSelectedFilters([])
        }
      }
    } catch (error) {
      console.error('Error loading config:', error)
      alert('Error al cargar la configuración')
    } finally {
      setLoading(false)
    }
  }

  const handleHeroImageChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) {
      setHeroImageFile(null)
      setHeroImagePreview(config?.hero_imagen_url || '')
      return
    }
    setHeroImageFile(file)
    const previewUrl = URL.createObjectURL(file)
    setHeroImagePreview(previewUrl)
  }

  const loadUbicanosData = async () => {
    try {
      setLoading(true)
      const { data: slides } = await db.getAllUbicanosSlider()
      const { data: cardsData } = await db.getAllUbicanosCards()
      setSliderImages(slides || [])
      setCards(cardsData || [])
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadHomeCards = async () => {
    try {
      const { data } = await db.getAllHomeCards()
      setHomeCards(data || [])
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const loadHomeBlock = async () => {
    try {
      const { data } = await db.getAllHomeBlock()
      setHomeBlock((data && data[0]) || null)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const loadBrandFilters = async () => {
    try {
      // Get all categories for tiendas
      const { data: categories } = await db.getCategorias('tiendas')
      if (!categories) return

      // Get all published stores to count by category
      const { data: tiendas } = await db.getTiendas()
      if (!tiendas) return

      // Count stores per category
      const categoryCounts = {}
      tiendas.forEach(tienda => {
        if (tienda.categoria?.id && tienda.categoria?.nombre) {
          const catId = tienda.categoria.id
          if (!categoryCounts[catId]) {
            categoryCounts[catId] = {
              id: catId,
              nombre: tienda.categoria.nombre,
              count: 0
            }
          }
          categoryCounts[catId].count += 1
        }
      })

      // Only show categories that have at least 1 published store
      const availableCats = Object.values(categoryCounts)
        .filter(cat => cat.count > 0)
        .sort((a, b) => a.nombre.localeCompare(b.nombre))

      setAvailableCategories(availableCats)
    } catch (error) {
      console.error('Error loading brand filters:', error)
    }
  }

  const handleToggleFilter = (category) => {
    const exists = selectedFilters.find(f => f.id === category.id)
    if (exists) {
      setSelectedFilters(selectedFilters.filter(f => f.id !== category.id))
    } else {
      if (selectedFilters.length >= 5) {
        alert('Máximo 5 filtros permitidos')
        return
      }
      setSelectedFilters([...selectedFilters, { id: category.id, nombre: category.nombre }])
    }
  }

  const moveFilterUp = (index) => {
    if (index === 0) return
    const newFilters = [...selectedFilters]
    ;[newFilters[index - 1], newFilters[index]] = [newFilters[index], newFilters[index - 1]]
    setSelectedFilters(newFilters)
  }

  const moveFilterDown = (index) => {
    if (index === selectedFilters.length - 1) return
    const newFilters = [...selectedFilters]
    ;[newFilters[index], newFilters[index + 1]] = [newFilters[index + 1], newFilters[index]]
    setSelectedFilters(newFilters)
  }

  // SEO Functions
  const loadSeoPages = async () => {
    try {
      setLoading(true)
      const pages = ['home', 'marcas', 'tiendas', 'eventos', 'ubicanos', 'contacto', 'trabaja', 'legal']
      setSeoPages(pages)
      await loadSeoConfig()
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSeoConfig = async () => {
    try {
      const { data, error } = await db.getSEOConfig(selectedSeoPage)
      if (error) throw error
      setSeoConfig(data || {
        pagina: selectedSeoPage,
        meta_title: '',
        meta_description: '',
        canonical_url: '',
        og_title: '',
        og_description: '',
        og_image_url: '',
        twitter_card: 'summary_large_image',
        robots_index: true,
        json_ld: ''
      })
    } catch (error) {
      console.error('Error loading SEO:', error)
    }
  }

  const handleSaveSeo = async () => {
    try {
      setSavingSeo(true)
      const { error } = await db.updateSEOConfig(selectedSeoPage, seoConfig)
      if (error) throw error
      alert('✅ Configuración SEO guardada exitosamente')
      await loadSeoConfig()
    } catch (error) {
      console.error('Error saving SEO:', error)
      alert('❌ Error al guardar la configuración SEO')
    } finally {
      setSavingSeo(false)
    }
  }

  useEffect(() => {
    if (selectedPage === 'seo') {
      loadSeoConfig()
    }
  }, [selectedSeoPage])

  const handleSaveSlide = async (slideData, imageFile) => {
    try {
      let imageUrl = slideData.imagen_url
      
      if (imageFile) {
        const safeName = imageFile.name.replace(/\s+/g, '_')
        const path = `ubicanos/portadas/${Date.now()}_${safeName}`
        const { error: uploadError } = await storage.uploadImage('assets', imageFile, path)
        if (uploadError) throw uploadError
        imageUrl = storage.getPublicUrl('assets', path)
      }

      const dataToSave = { ...slideData, imagen_url: imageUrl }
      
      if (editingSlide) {
        await db.updateUbicanosSlide(editingSlide.id, dataToSave)
      } else {
        await db.createUbicanosSlide(dataToSave)
      }
      
      setShowSlideModal(false)
      setEditingSlide(null)
      await loadUbicanosData()
      alert('Imagen guardada exitosamente')
    } catch (error) {
      console.error('Error:', error)
      alert('Error al guardar la imagen')
    }
  }

  const handleDeleteSlide = async (id) => {
    if (!confirm('¿Eliminar esta imagen?')) return
    try {
      await db.deleteUbicanosSlide(id)
      await loadUbicanosData()
      alert('Imagen eliminada')
    } catch (error) {
      console.error('Error:', error)
      alert('Error al eliminar')
    }
  }

  const handleToggleSlideActive = async (slide) => {
    try {
      await db.updateUbicanosSlide(slide.id, { activo: !slide.activo })
      await loadUbicanosData()
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleSaveCard = async (cardData) => {
    try {
      if (editingCard) {
        await db.updateUbicanosCard(editingCard.id, cardData)
      } else {
        await db.createUbicanosCard(cardData)
      }
      
      setShowCardModal(false)
      setEditingCard(null)
      await loadUbicanosData()
      alert('Tarjeta guardada exitosamente')
    } catch (error) {
      console.error('Error:', error)
      alert('Error al guardar la tarjeta')
    }
  }

  const handleDeleteCard = async (id) => {
    if (!confirm('¿Eliminar esta tarjeta?')) return
    try {
      await db.deleteUbicanosCard(id)
      await loadUbicanosData()
      alert('Tarjeta eliminada')
    } catch (error) {
      console.error('Error:', error)
      alert('Error al eliminar')
    }
  }

  const handleToggleCardActive = async (card) => {
    try {
      await db.updateUbicanosCard(card.id, { activo: !card.activo })
      await loadUbicanosData()
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleSave = async () => {
    if (!config) return
    
    setSaving(true)
    try {
      let updatedData = { ...config }
      delete updatedData.id
      delete updatedData.created_at
      delete updatedData.updated_at
      delete updatedData.pagina

      // If on home page, include brand_filters
      if (selectedPage === 'home') {
        updatedData.brand_filters = selectedFilters
      }

      // Upload hero image if new file selected
      if (heroImageFile) {
        const safeName = heroImageFile.name.replace(/\s+/g, '_')
        const path = `${selectedPage}/portadas/${Date.now()}_${safeName}`
        const { error: uploadError } = await storage.uploadImage('assets', heroImageFile, path)
        
        if (uploadError) throw uploadError
        
        const publicUrl = storage.getPublicUrl('assets', path)
        updatedData.hero_imagen_url = publicUrl
      }

      // Update config
      const { error } = await db.updateConfiguracionPortada(selectedPage, updatedData)
      
      if (error) throw error

      alert('Configuración guardada exitosamente ✅')
      setHeroImageFile(null)
      await loadConfig()
    } catch (error) {
      console.error('Error saving:', error)
      alert('Error al guardar la configuración')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    )
  }

  if (!config && selectedPage !== 'ubicanos' && selectedPage !== 'seo') {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">No se encontró configuración para esta página</p>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">WEBSITE</h1>
        <p className="text-gray-600 mt-1">
          Administra las portadas (hero sections) y textos de las páginas principales
        </p>
      </div>

      {/* Page Selector */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <label className="block text-sm font-medium mb-3">Selecciona la página a configurar:</label>
          <div className="flex gap-3">
            {pages.map((page) => (
              <button
                key={page.id}
                onClick={() => setSelectedPage(page.id)}
                className={`flex-1 px-6 py-4 rounded-lg border-2 transition-all ${
                  selectedPage === page.id
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <div className="text-3xl mb-2">{page.icon}</div>
                <div className="font-semibold">{page.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Configuration Form - Only for Home, Tiendas, Eventos */}
      {selectedPage !== 'ubicanos' && selectedPage !== 'seo' && (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">
            Configuración de "{pages.find(p => p.id === selectedPage)?.label}"
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Hero Section */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-semibold mb-4">📸 Portada (Hero Section)</h3>
            
            {/* Hero Image */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Imagen de fondo de la portada
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleHeroImageChange}
                className="w-full px-3 py-2 border rounded-lg mb-3"
              />
              {heroImagePreview && (
                <div className="mt-3">
                  <img
                    src={heroImagePreview}
                    alt="Vista previa"
                    className="w-full aspect-[4/3] object-cover rounded-lg border"
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                📏 Recomendado: 1200x900px (proporción 4:3)
              </p>
            </div>

            {/* Hero Image Alt */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Texto alternativo de la imagen (SEO)
              </label>
              <input
                type="text"
                value={config.hero_imagen_alt || ''}
                onChange={(e) => setConfig({ ...config, hero_imagen_alt: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ej: Plaza Aurora"
              />
            </div>

            {/* Hero Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Título principal *
              </label>
              <textarea
                rows={2}
                value={config.hero_titulo || ''}
                onChange={(e) => setConfig({ ...config, hero_titulo: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ej: Descubre Plaza Aurora: Tu Outlet Favorito"
              />
            </div>

            {/* Hero Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Descripción / Subtítulo
              </label>
              <textarea
                rows={2}
                value={config.hero_descripcion || ''}
                onChange={(e) => setConfig({ ...config, hero_descripcion: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ej: Donde las mejores marcas se encuentran con los mejores precios 🛍️"
              />
            </div>
          </div>

          {/* Buttons Section */}
          <div className="border-b pb-6">
            <h3 className="text-lg font-semibold mb-4">🔘 Botones de la Portada</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Button 1 */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Botón 1 (Principal)</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Texto del botón</label>
                    <input
                      type="text"
                      value={config.hero_boton_1_texto || ''}
                      onChange={(e) => setConfig({ ...config, hero_boton_1_texto: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Ej: Descubre Descuentos"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">URL / Enlace</label>
                    <input
                      type="text"
                      value={config.hero_boton_1_url || ''}
                      onChange={(e) => setConfig({ ...config, hero_boton_1_url: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Ej: /marcas"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Estilo</label>
                    <select
                      value={config.hero_boton_1_estilo || 'primary'}
                      onChange={(e) => setConfig({ ...config, hero_boton_1_estilo: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="primary">Primary (Relleno)</option>
                      <option value="outline">Outline (Borde)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Button 2 */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Botón 2 (Secundario)</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Texto del botón</label>
                    <input
                      type="text"
                      value={config.hero_boton_2_texto || ''}
                      onChange={(e) => setConfig({ ...config, hero_boton_2_texto: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Ej: Explora Eventos"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">URL / Enlace</label>
                    <input
                      type="text"
                      value={config.hero_boton_2_url || ''}
                      onChange={(e) => setConfig({ ...config, hero_boton_2_url: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Ej: /eventos"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Estilo</label>
                    <select
                      value={config.hero_boton_2_estilo || 'outline'}
                      onChange={(e) => setConfig({ ...config, hero_boton_2_estilo: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="primary">Primary (Relleno)</option>
                      <option value="outline">Outline (Borde)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Marquee Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">🎯 Franja Negra (Marquee)</h3>
            
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={config.marquee_activo || false}
                  onChange={(e) => setConfig({ ...config, marquee_activo: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">
                  Mostrar franja negra (marquee)
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Texto de la franja (se repite automáticamente)
              </label>
              <textarea
                rows={3}
                value={config.marquee_texto || ''}
                onChange={(e) => setConfig({ ...config, marquee_texto: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ej: ★ LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS  •  LAS MEJORES MARCAS  •  LOS MEJORES PRECIOS"
              />
              <p className="text-xs text-gray-500 mt-2">
                💡 Usa símbolos como ★ • y espacios para separar los elementos
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">
                Enlace de la franja (URL opcional)
              </label>
              <input
                type="text"
                value={config.marquee_link_url || ''}
                onChange={(e) => setConfig({ ...config, marquee_link_url: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Ej: /ubicanos o https://miweb.com/promos"
              />
              <p className="text-xs text-gray-500 mt-2">
                🔗 Si comienza con "/", se usa navegación interna; URLs externas abren en nueva pestaña. Deja vacío si no deseas que sea clickeable.
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="p-6 border-t bg-gray-50">
          <div className="flex justify-end gap-3">
            <button
              onClick={loadConfig}
              className="px-6 py-2 border rounded-lg hover:bg-gray-100"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Guardando...' : '💾 Guardar Cambios'}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Home Cards Management (only in Home tab) */}
      {selectedPage === 'home' && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="p-6 border-b flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Tarjetas de Home</h2>
              <p className="text-sm text-gray-600 mt-1">Gestiona las tarjetas "Explora tiendas" y "Explora eventos"</p>
            </div>
            <button
              onClick={() => { setEditingHomeCard(null); setShowHomeCardModal(true); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              + Agregar/Editar Tarjeta
            </button>
          </div>
          <div className="p-6">
            {homeCards.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No hay tarjetas configuradas</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {homeCards.map((card) => (
                  <div key={card.id} className="border rounded-lg overflow-hidden">
                    <div className="relative aspect-[4/3] bg-gray-200">
                      <img src={card.imagen_url} alt={card.alt_text || card.texto} className="w-full h-full object-cover" />
                      <div className="absolute top-2 left-2 flex gap-2">
                        <span className="px-2 py-1 bg-black bg-opacity-60 text-white text-xs rounded">Tipo: {card.tipo}</span>
                        <button
                          onClick={async () => { await db.updateHomeCard(card.id, { activo: !card.activo }); loadHomeCards(); }}
                          className={`px-2 py-1 text-xs rounded ${card.activo ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}
                        >
                          {card.activo ? '✓ Activo' : 'Inactivo'}
                        </button>
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-sm mb-1">{card.texto}</h4>
                      <p className="text-xs text-gray-600">{card.enlace_url || '(sin enlace)'}</p>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => { setEditingHomeCard(card); setShowHomeCardModal(true); }}
                          className="flex-1 px-3 py-1 text-xs border rounded hover:bg-gray-50"
                        >
                          Editar
                        </button>
                        <button
                          onClick={async () => { if (confirm('¿Eliminar esta tarjeta?')) { await db.deleteHomeCard(card.id); loadHomeCards(); } }}
                          className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Home Block Management (only in Home tab) */}
      {selectedPage === 'home' && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="p-6 border-b flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Bloque negro de Home</h2>
              <p className="text-sm text-gray-600 mt-1">Configura la imagen, el texto y el botón del bloque final</p>
            </div>
            <button
              onClick={() => setShowHomeBlockModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {homeBlock ? 'Editar Bloque' : 'Crear Bloque'}
            </button>
          </div>
          <div className="p-6">
            {!homeBlock ? (
              <p className="text-center text-gray-500 py-8">Aún no hay bloque configurado</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg overflow-hidden">
                  <div className="relative aspect-[4/3] bg-gray-200">
                    <img src={homeBlock.imagen_url} alt={homeBlock.alt_text || homeBlock.titulo} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <h4 className="font-medium text-sm mb-1">{homeBlock.titulo || 'Sin título'}</h4>
                    <p className="text-xs text-gray-600 mb-2">{homeBlock.descripcion || '(Sin descripción)'}</p>
                    <div className="flex gap-2 items-center">
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">{homeBlock.boton_activo ? 'Botón activo' : 'Botón inactivo'}</span>
                      {homeBlock.boton_activo && (
                        <span className="text-xs text-gray-600">{homeBlock.boton_texto} → {homeBlock.boton_url || '(sin URL)'}</span>
                      )}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={async () => { await db.updateHomeBlock(homeBlock.id, { activo: !homeBlock.activo }); loadHomeBlock(); }}
                        className={`px-3 py-1 text-xs rounded ${homeBlock.activo ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}
                      >
                        {homeBlock.activo ? '✓ Activo' : 'Inactivo'}
                      </button>
                      <button
                        onClick={() => setShowHomeBlockModal(true)}
                        className="flex-1 px-3 py-1 text-xs border rounded hover:bg-gray-50"
                      >
                        Editar
                      </button>
                      <button
                        onClick={async () => { if (confirm('¿Eliminar este bloque?')) { await db.deleteHomeBlock(homeBlock.id); loadHomeBlock(); }}}
                        className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Brand Filters Configuration (only in Home tab) */}
      {selectedPage === 'home' && (
        <div className="bg-white rounded-lg shadow mt-6">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold">Filtros de Tiendas</h2>
            <p className="text-sm text-gray-600 mt-1">
              Configura los filtros que aparecerán en la sección "Las Mejores Tiendas" (máximo 5)
            </p>
          </div>
          <div className="p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>💡 Información:</strong> Los filtros mostrados son las categorías que actualmente tienen tiendas publicadas. 
                Para habilitar más opciones, agrega nuevas categorías en el módulo de Tiendas.
              </p>
            </div>

            {availableCategories.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No hay categorías disponibles con tiendas publicadas</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Available Categories */}
                <div>
                  <h3 className="font-medium mb-3">
                    Categorías Disponibles ({selectedFilters.length}/5 seleccionados)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableCategories.map((category) => {
                      const isSelected = selectedFilters.some(f => f.id === category.id)
                      return (
                        <button
                          key={category.id}
                          onClick={() => handleToggleFilter(category)}
                          disabled={!isSelected && selectedFilters.length >= 5}
                          className={`px-4 py-3 rounded-lg border-2 transition-all text-left ${
                            isSelected
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">{category.nombre}</div>
                              <div className="text-xs text-gray-500 mt-1">
                                {category.count} tienda{category.count !== 1 ? 's' : ''}
                              </div>
                            </div>
                            {isSelected && (
                              <span className="text-blue-600 text-xl">✓</span>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Selected Filters (with ordering) */}
                {selectedFilters.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-3">
                      Orden de Filtros en la Web (arrastra para reordenar)
                    </h3>
                    <div className="space-y-2">
                      {selectedFilters.map((filter, index) => (
                        <div
                          key={filter.id}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
                        >
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => moveFilterUp(index)}
                              disabled={index === 0}
                              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => moveFilterDown(index)}
                              disabled={index === selectedFilters.length - 1}
                              className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              ▼
                            </button>
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">{filter.nombre}</div>
                            <div className="text-xs text-gray-500">Posición: {index + 1}</div>
                          </div>
                          <button
                            onClick={() => handleToggleFilter(filter)}
                            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Quitar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedFilters.length === 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>⚠️ Atención:</strong> No has seleccionado ningún filtro. 
                      El sistema mostrará automáticamente los 5 filtros con más tiendas.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ubícanos Management Section */}
      {selectedPage === 'ubicanos' && (
        <div className="space-y-6">
          {/* Slider Management */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Slider de Imágenes</h2>
                <p className="text-sm text-gray-600 mt-1">Gestiona las imágenes del slider de Ubícanos</p>
              </div>
              <button
                onClick={() => { setEditingSlide(null); setShowSlideModal(true); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                + Agregar Imagen
              </button>
            </div>
            <div className="p-6">
              {sliderImages.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay imágenes en el slider</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sliderImages.map((slide) => (
                    <div key={slide.id} className="border rounded-lg overflow-hidden">
                      <div className="relative h-48 bg-gray-200">
                        <img src={slide.imagen_url} alt={slide.alt_text || slide.titulo} className="w-full h-full object-cover" />
                        <div className="absolute top-2 left-2 flex gap-2">
                          <span className="px-2 py-1 bg-black bg-opacity-60 text-white text-xs rounded">
                            Orden: {slide.orden}
                          </span>
                          <button
                            onClick={() => handleToggleSlideActive(slide)}
                            className={`px-2 py-1 text-xs rounded ${slide.activo ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}
                          >
                            {slide.activo ? '✓ Activo' : 'Inactivo'}
                          </button>
                        </div>
                      </div>
                      <div className="p-3">
                        <h4 className="font-medium text-sm mb-1">{slide.titulo || 'Sin título'}</h4>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => { setEditingSlide(slide); setShowSlideModal(true); }}
                            className="flex-1 px-3 py-1 text-xs border rounded hover:bg-gray-50"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteSlide(slide.id)}
                            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Cards Management */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Tarjetas de Información</h2>
                <p className="text-sm text-gray-600 mt-1">Gestiona las tarjetas informativas (Dirección, Horario, etc.)</p>
              </div>
              <button
                onClick={() => { setEditingCard(null); setShowCardModal(true); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                + Agregar Tarjeta
              </button>
            </div>
            <div className="p-6">
              {cards.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay tarjetas configuradas</p>
              ) : (
                <div className="space-y-3">
                  {cards.map((card) => (
                    <div key={card.id} className="border rounded-lg p-4 flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium">
                          {card.icono && `${card.icono} `}{card.titulo}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">{card.tipo}</p>
                        <p className="text-sm mt-2">{card.contenido}</p>
                        <div className="flex gap-2 mt-2">
                          <span className="text-xs bg-gray-100 px-2 py-1 rounded">Orden: {card.orden}</span>
                          <button
                            onClick={() => handleToggleCardActive(card)}
                            className={`text-xs px-2 py-1 rounded ${card.activo ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}
                          >
                            {card.activo ? '✓ Activo' : 'Inactivo'}
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => { setEditingCard(card); setShowCardModal(true); }}
                          className="px-3 py-1 text-xs border rounded hover:bg-gray-50"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteCard(card.id)}
                          className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SEO Configuration Section */}
      {selectedPage === 'seo' && seoConfig && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold">Configuración SEO por Página</h2>
            <p className="text-sm text-gray-600 mt-1">Optimiza cómo aparece tu sitio en Google y redes sociales</p>
          </div>

          {/* Page Selector for SEO */}
          <div className="p-6 border-b bg-gray-50">
            <label className="block text-sm font-medium mb-3">Selecciona la página a optimizar:</label>
            <select
              value={selectedSeoPage}
              onChange={(e) => setSelectedSeoPage(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg bg-white"
            >
              <option value="home">🏠 Home</option>
              <option value="marcas">🏪 Tiendas (Marcas)</option>
              <option value="tiendas">🏬 Tiendas2</option>
              <option value="eventos">🎉 Eventos</option>
              <option value="descuentos">🏷️ Descuentos</option>
              <option value="ubicanos">📍 Ubícanos</option>
              <option value="contacto">✉️ Contacto</option>
              <option value="trabaja">👥 Trabaja con Nosotros</option>
              <option value="legal">📜 Legal</option>
            </select>
          </div>

          <div className="p-6 space-y-6">
            {/* Meta Tags Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">🔖 Meta Tags (Google)</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Título SEO * <span className="text-gray-500 text-xs">(50-60 caracteres ideal)</span>
                  </label>
                  <input
                    type="text"
                    value={seoConfig.meta_title || ''}
                    onChange={(e) => setSeoConfig({ ...seoConfig, meta_title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Ej: Plaza Aurora - Tu Outlet Favorito"
                    maxLength="70"
                  />
                  <p className="text-xs text-gray-500 mt-1">{seoConfig.meta_title?.length || 0}/70 caracteres</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Meta Description * <span className="text-gray-500 text-xs">(150-160 caracteres ideal)</span>
                  </label>
                  <textarea
                    rows={3}
                    value={seoConfig.meta_description || ''}
                    onChange={(e) => setSeoConfig({ ...seoConfig, meta_description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Ej: Descubre las mejores marcas a precios outlet en Lima Norte. ¡Visita Plaza Aurora y encuentra moda, tecnología y más!"
                    maxLength="160"
                  />
                  <p className="text-xs text-gray-500 mt-1">{seoConfig.meta_description?.length || 0}/160 caracteres</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    URL Canónica <span className="text-gray-500 text-xs">(URL completa de esta página)</span>
                  </label>
                  <input
                    type="url"
                    value={seoConfig.canonical_url || ''}
                    onChange={(e) => setSeoConfig({ ...seoConfig, canonical_url: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="https://plazaaurora.pe/"
                  />
                </div>
              </div>
            </div>

            {/* Open Graph Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">👥 Open Graph (Facebook, LinkedIn)</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Título OG</label>
                  <input
                    type="text"
                    value={seoConfig.og_title || ''}
                    onChange={(e) => setSeoConfig({ ...seoConfig, og_title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Dejar vacío usa el título SEO"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Descripción OG</label>
                  <textarea
                    rows={2}
                    value={seoConfig.og_description || ''}
                    onChange={(e) => setSeoConfig({ ...seoConfig, og_description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Dejar vacío usa la meta description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Imagen OG <span className="text-gray-500 text-xs">(1200x630px recomendado)</span>
                  </label>
                  <input
                    type="url"
                    value={seoConfig.og_image_url || ''}
                    onChange={(e) => setSeoConfig({ ...seoConfig, og_image_url: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="URL completa de la imagen"
                  />
                  {seoConfig.og_image_url && (
                    <img src={seoConfig.og_image_url} alt="Preview OG" className="mt-3 w-full max-w-md h-48 object-cover rounded-lg border" />
                  )}
                </div>
              </div>
            </div>

            {/* Twitter Card Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">🐦 Twitter Card</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">Tipo de Card</label>
                <select
                  value={seoConfig.twitter_card || 'summary_large_image'}
                  onChange={(e) => setSeoConfig({ ...seoConfig, twitter_card: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="summary_large_image">Summary Large Image (Recomendado)</option>
                  <option value="summary">Summary</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Twitter usará el título, descripción e imagen OG</p>
              </div>
            </div>

            {/* Robots Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">🤖 Indexación</h3>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="robots-index"
                  checked={seoConfig.robots_index !== false}
                  onChange={(e) => setSeoConfig({ ...seoConfig, robots_index: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="robots-index" className="text-sm">
                  Permitir que Google indexe esta página (Recomendado: activado)
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                ⚠️ Si desactivas esto, esta página NO aparecerá en resultados de Google
              </p>
            </div>

            {/* JSON-LD Section */}
            <div className="pb-6">
              <h3 className="text-lg font-semibold mb-4">🧩 Datos Estructurados (Avanzado)</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  JSON-LD Schema <span className="text-gray-500 text-xs">(Opcional - Para usuarios avanzados)</span>
                </label>
                <textarea
                  rows={6}
                  value={seoConfig.json_ld || ''}
                  onChange={(e) => setSeoConfig({ ...seoConfig, json_ld: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-xs"
                  placeholder='{\n  "@context": "https://schema.org",\n  "@type": "Organization",\n  "name": "Plaza Aurora Outlet"\n}'
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Esto es opcional. Si lo dejas vacío, el sistema usará valores predeterminados.
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleSaveSeo}
                disabled={savingSeo}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingSeo ? 'Guardando...' : '💾 Guardar Configuración SEO'}
              </button>
            </div>
          </div>

          {/* SEO Preview */}
          <div className="p-6 bg-gray-50 border-t">
            <h3 className="text-lg font-semibold mb-4">👁️ Vista Previa de Google</h3>
            <div className="bg-white border rounded-lg p-4 max-w-2xl">
              <div className="text-blue-800 text-xl hover:underline cursor-pointer">
                {seoConfig.meta_title || 'Título de la página'}
              </div>
              <div className="text-green-700 text-sm mt-1">
                {seoConfig.canonical_url || 'https://plazaaurora.pe'}
              </div>
              <div className="text-gray-600 text-sm mt-2">
                {seoConfig.meta_description || 'Descripción de la página que aparecerá en Google...'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <span className="text-2xl mr-3">💡</span>
          <div>
            <p className="font-medium text-blue-900">Consejos de configuración</p>
            <ul className="text-sm text-blue-800 mt-2 space-y-1 ml-4">
              <li>• Las imágenes se guardan en: <code className="bg-blue-100 px-1 rounded">assets/{selectedPage}/portadas/</code></li>
              <li>• Usa imágenes de alta calidad y optimizadas para web</li>
              <li>• Los botones son opcionales - puedes dejarlos vacíos si no los necesitas</li>
              <li>• El texto del marquee se repite automáticamente en bucle</li>
              <li>• Los cambios se reflejan inmediatamente en la página pública</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Slide Modal */}
      {showSlideModal && <SlideModal slide={editingSlide} onSave={handleSaveSlide} onClose={() => { setShowSlideModal(false); setEditingSlide(null); }} />}

      {/* Card Modal */}
      {showCardModal && <CardModal card={editingCard} onSave={handleSaveCard} onClose={() => { setShowCardModal(false); setEditingCard(null); }} />}

      {/* Home Card Modal */}
      {showHomeCardModal && <HomeCardModal card={editingHomeCard} onClose={() => { setShowHomeCardModal(false); setEditingHomeCard(null); loadHomeCards(); }} />}

      {/* Home Block Modal */}
      {showHomeBlockModal && <HomeBlockModal block={homeBlock} onClose={() => { setShowHomeBlockModal(false); loadHomeBlock(); }} />}
    </div>
  )
}

// Slide Modal Component
function SlideModal({ slide, onSave, onClose }) {
  const [formData, setFormData] = useState({
    titulo: slide?.titulo || '',
    alt_text: slide?.alt_text || '',
    enlace_url: slide?.enlace_url || '',
    orden: slide?.orden || 0,
    activo: slide?.activo !== undefined ? slide.activo : true,
    imagen_url: slide?.imagen_url || ''
  })
  const [imageFile, setImageFile] = useState(null)
  const [preview, setPreview] = useState(slide?.imagen_url || '')

  const handleImageChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setPreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!slide && !imageFile) {
      alert('Debes seleccionar una imagen')
      return
    }
    onSave(formData, imageFile)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-h-[90vh] overflow-y-auto" style={{ maxWidth: window.innerWidth < 768 ? '100%' : '672px' }}>
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold">{slide ? 'Editar' : 'Agregar'} Imagen</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Imagen *</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className="w-full px-3 py-2 border rounded-lg" />
            {preview && <img src={preview} alt="Preview" className="mt-3 w-full aspect-[4/3] object-cover rounded-lg border" />}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Título</label>
            <input type="text" value={formData.titulo} onChange={(e) => setFormData({...formData, titulo: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Texto alternativo (Alt)</label>
            <input type="text" value={formData.alt_text} onChange={(e) => setFormData({...formData, alt_text: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Enlace URL (opcional)</label>
            <input type="text" value={formData.enlace_url} onChange={(e) => setFormData({...formData, enlace_url: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="https://..." />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Orden</label>
            <input type="number" value={formData.orden} onChange={(e) => setFormData({...formData, orden: parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.activo} onChange={(e) => setFormData({...formData, activo: e.target.checked})} className="w-4 h-4" />
              <span className="text-sm font-medium">Activo</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancelar</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Card Modal Component
function CardModal({ card, onSave, onClose }) {
  const [formData, setFormData] = useState({
    tipo: card?.tipo || '',
    titulo: card?.titulo || '',
    contenido: card?.contenido || '',
    icono: card?.icono || '',
    orden: card?.orden || 0,
    activo: card?.activo !== undefined ? card.activo : true,
    button_activo: card?.button_activo || false,
    button_url: card?.button_url || '',
    button_text: card?.button_text || ''
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.titulo || !formData.contenido) {
      alert('Título y contenido son obligatorios')
      return
    }
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-h-[90vh] overflow-y-auto" style={{ maxWidth: window.innerWidth < 768 ? '100%' : '672px' }}>
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold">{card ? 'Editar' : 'Agregar'} Tarjeta</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tipo *</label>
            <select value={formData.tipo} onChange={(e) => setFormData({...formData, tipo: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
              <option value="">Seleccionar tipo</option>
              <option value="direccion">Dirección</option>
              <option value="horario">Horario</option>
              <option value="contacto">Contacto</option>
              <option value="nota">Nota</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Título *</label>
            <input type="text" value={formData.titulo} onChange={(e) => setFormData({...formData, titulo: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ej: DIRECCIÓN" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Contenido *</label>
            <textarea rows={4} value={formData.contenido} onChange={(e) => setFormData({...formData, contenido: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ej: Av. Carlos Izaguirre 271-275, Independencia, Lima, Perú" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Icono (emoji)</label>
            <input type="text" value={formData.icono} onChange={(e) => setFormData({...formData, icono: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ej: 📍" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Orden</label>
            <input type="number" value={formData.orden} onChange={(e) => setFormData({...formData, orden: parseInt(e.target.value)})} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.activo} onChange={(e) => setFormData({...formData, activo: e.target.checked})} className="w-4 h-4" />
              <span className="text-sm font-medium">Activo</span>
            </label>
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.button_activo}
                onChange={(e) => setFormData({ ...formData, button_activo: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium">Mostrar botón en la tarjeta</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Si está activo, se mostrará un botón único en la tarjeta.
            </p>
          </div>

          {formData.button_activo && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-2">Texto del botón</label>
                <input
                  type="text"
                  value={formData.button_text}
                  onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Ej: Abrir en Waze"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">URL del botón</label>
                <input
                  type="text"
                  value={formData.button_url}
                  onChange={(e) => setFormData({ ...formData, button_url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Ej: https://waze.com/ul?ll=... o /ubicanos"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si comienza con "/", se usa navegación interna; URLs externas abren en nueva pestaña.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancelar</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Home Card Modal Component
function HomeCardModal({ card, onClose }) {
  const [formData, setFormData] = useState({
    tipo: card?.tipo || '',
    texto: card?.texto || '',
    enlace_url: card?.enlace_url || '',
    activo: card?.activo !== undefined ? card.activo : true,
    imagen_url: card?.imagen_url || '',
    alt_text: card?.alt_text || ''
  })
  const [imageFile, setImageFile] = useState(null)
  const [preview, setPreview] = useState(card?.imagen_url || '')

  const handleImageChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setPreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.tipo) { alert('Selecciona tipo: tiendas o eventos'); return }
    if (!card && !imageFile) { alert('Debes seleccionar una imagen'); return }

    try {
      let imageUrl = formData.imagen_url
      if (imageFile) {
        const safeName = imageFile.name.replace(/\s+/g, '_')
        const path = `tarjetas/${Date.now()}_${safeName}`
        const { error: uploadError } = await storage.uploadImage('assets', imageFile, path)
        if (uploadError) throw uploadError
        imageUrl = storage.getPublicUrl('assets', path)
      }

      const payload = { ...formData, imagen_url: imageUrl }

      if (card?.id) {
        await db.updateHomeCard(card.id, payload)
      } else {
        await db.createHomeCard(payload)
      }

      alert('Tarjeta guardada exitosamente')
      onClose()
    } catch (error) {
      console.error('Error:', error)
      alert('Error al guardar la tarjeta')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-h-[90vh] overflow-y-auto" style={{ maxWidth: window.innerWidth < 768 ? '100%' : '672px' }}>
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold">{card ? 'Editar' : 'Agregar'} Tarjeta</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tipo *</label>
            <select value={formData.tipo} onChange={(e) => setFormData({...formData, tipo: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
              <option value="">Seleccionar tipo</option>
              <option value="tiendas">Tiendas</option>
              <option value="eventos">Eventos</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Texto *</label>
            <input type="text" value={formData.texto} onChange={(e) => setFormData({...formData, texto: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ej: Explora tiendas" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">URL (opcional)</label>
            <input type="text" value={formData.enlace_url} onChange={(e) => setFormData({...formData, enlace_url: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="/marcas o https://..." />
            <p className="text-xs text-gray-500 mt-1">Si comienza con "/", navegación interna; externas abren en nueva pestaña.</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Imagen *</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className="w-full px-3 py-2 border rounded-lg" />
            {preview && <img src={preview} alt="Preview" className="mt-3 w-full aspect-[4/3] object-cover rounded-lg border" />}
            <p className="text-xs text-gray-500 mt-1">📏 Recomendado: 1200x900px (proporción 4:3)</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Alt text (SEO)</label>
            <input type="text" value={formData.alt_text} onChange={(e) => setFormData({...formData, alt_text: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancelar</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Home Block Modal
function HomeBlockModal({ block, onClose }) {
  const [formData, setFormData] = useState({
    activo: block?.activo ?? true,
    titulo: block?.titulo || '',
    descripcion: block?.descripcion || '',
    boton_activo: block?.boton_activo || false,
    boton_texto: block?.boton_texto || '',
    boton_url: block?.boton_url || '',
    imagen_url: block?.imagen_url || '',
    alt_text: block?.alt_text || ''
  })
  const [imageFile, setImageFile] = useState(null)
  const [preview, setPreview] = useState(block?.imagen_url || '')

  const handleImageChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setPreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      let imageUrl = formData.imagen_url
      if (imageFile) {
        const safeName = imageFile.name.replace(/\s+/g, '_')
        const path = `home/bloque/${Date.now()}_${safeName}`
        const { error: uploadError } = await storage.uploadImage('assets', imageFile, path)
        if (uploadError) throw uploadError
        imageUrl = storage.getPublicUrl('assets', path)
      }

      const payload = { ...formData, imagen_url: imageUrl }

      if (block?.id) {
        await db.updateHomeBlock(block.id, payload)
      } else {
        await db.createHomeBlock(payload)
      }

      alert('Bloque guardado exitosamente')
      onClose()
    } catch (error) {
      console.error('Error:', error)
      alert('Error al guardar el bloque')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-h-[90vh] overflow-y-auto" style={{ maxWidth: window.innerWidth < 768 ? '100%' : '672px' }}>
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold">{block?.id ? 'Editar Bloque' : 'Crear Bloque'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.activo} onChange={(e) => setFormData({ ...formData, activo: e.target.checked })} className="w-4 h-4" />
              <span className="text-sm font-medium">Activo</span>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Título *</label>
            <textarea rows={2} value={formData.titulo} onChange={(e) => setFormData({ ...formData, titulo: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="TU MARCA EN PLAZA AURORA" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Descripción *</label>
            <textarea rows={3} value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="Únete a las marcas..." />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Imagen *</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className="w-full px-3 py-2 border rounded-lg" />
            {preview && <img src={preview} alt="Preview" className="mt-3 w-full aspect-[4/3] object-cover rounded-lg border" />}
            <p className="text-xs text-gray-500 mt-1">📏 Recomendado: 1200x900px (proporción 4:3)</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Alt text (SEO)</label>
            <input type="text" value={formData.alt_text} onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.boton_activo} onChange={(e) => setFormData({ ...formData, boton_activo: e.target.checked })} className="w-4 h-4" />
              <span className="text-sm font-medium">Mostrar botón</span>
            </label>
          </div>
          {formData.boton_activo && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-2">Texto del botón</label>
                <input type="text" value={formData.boton_texto} onChange={(e) => setFormData({ ...formData, boton_texto: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="Contáctanos" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">URL del botón</label>
                <input type="text" value={formData.boton_url} onChange={(e) => setFormData({ ...formData, boton_url: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="/contacto o https://..." />
                <p className="text-xs text-gray-500 mt-1">Si comienza con "/", navegación interna; externas abren en nueva pestaña.</p>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancelar</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

